import React from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import RouteAnnouncer from "./components/base/RouteAnnouncer";
import PageViewTracker from "./components/base/PageViewTracker";
import CookieConsentBanner from "./components/consent/CookieConsentBanner";
import ErrorBoundary from "./components/ui/ErrorBoundary";
import LoadingSpinner from "./components/ui/LoadingSpinner";
import Home from "./pages/Home";
import Pricing from "./pages/Pricing";
import { ADMIN_ORIGIN, PRODUCT_ORIGIN, productUrl } from "./lib/appUrls";

const Contact = React.lazy(() => import("./pages/Contact"));
const Changelog = React.lazy(() => import("./pages/Changelog"));
const Terms = React.lazy(() => import("./pages/Terms"));
const Privacy = React.lazy(() => import("./pages/Privacy"));
const Dpa = React.lazy(() => import("./pages/Dpa"));
const RefundPolicy = React.lazy(() => import("./pages/RefundPolicy"));
const Industries = React.lazy(() => import("./pages/Industries"));
const IndustryLandingPage = React.lazy(() => import("./pages/IndustryLandingPage"));
const GuidePage = React.lazy(() => import("./pages/GuidePage"));
const BlogIndex = React.lazy(() => import("./pages/BlogIndex"));
const BlogPost = React.lazy(() => import("./pages/BlogPost"));
const ExtrusionCapacityCalculator = React.lazy(() => import("./pages/ExtrusionCapacityCalculator"));
const NotFound = React.lazy(() => import("./pages/NotFound"));

function ExternalRedirect({ origin }: { origin: string }) {
  React.useEffect(() => {
    const path = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.location.replace(origin === PRODUCT_ORIGIN ? productUrl(path) : `${origin}${path}`);
  }, [origin]);
  return <LoadingSpinner message="Opening RunMark…" />;
}

function AppRoutes() {
  const { t } = useTranslation();
  return (
    <ErrorBoundary>
      <React.Suspense fallback={<LoadingSpinner message={t("common.status.loading")} />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/home" element={<Navigate to="/" replace />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/changelog" element={<Changelog />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/dpa" element={<Dpa />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/industries" element={<Industries />} />
          <Route path="/industries/:slug" element={<IndustryLandingPage />} />
          <Route path="/production-scheduling-software" element={<GuidePage />} />
          <Route path="/compare/:slug" element={<GuidePage />} />
          <Route path="/guides/:slug" element={<GuidePage />} />
          <Route path="/blog" element={<BlogIndex />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/tools/extrusion-production-capacity-calculator" element={<ExtrusionCapacityCalculator />} />
          <Route path="/admin/*" element={<ExternalRedirect origin={ADMIN_ORIGIN} />} />
          <Route path="/login" element={<ExternalRedirect origin={PRODUCT_ORIGIN} />} />
          <Route path="/register" element={<ExternalRedirect origin={PRODUCT_ORIGIN} />} />
          <Route path="/logout" element={<ExternalRedirect origin={PRODUCT_ORIGIN} />} />
          <Route path="/dashboard/*" element={<ExternalRedirect origin={PRODUCT_ORIGIN} />} />
          <Route path="/assistant" element={<ExternalRedirect origin={PRODUCT_ORIGIN} />} />
          <Route path="/floor" element={<ExternalRedirect origin={PRODUCT_ORIGIN} />} />
          <Route path="/lot/:orgId/:scheduleBlockId" element={<ExternalRedirect origin={PRODUCT_ORIGIN} />} />
          <Route path="/email-preferences" element={<ExternalRedirect origin={PRODUCT_ORIGIN} />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </React.Suspense>
    </ErrorBoundary>
  );
}

export default function App() {
  const { t } = useTranslation();
  const handleSkipToMain = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    const target = document.getElementById("main-content") || document.querySelector<HTMLElement>("main");
    if (!target) return;
    if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
    target.focus();
  };
  return (
    <BrowserRouter>
      <a href="#main-content" className="skip-link" onClick={handleSkipToMain}>
        {t("common.actions.skipToMain")}
      </a>
      <RouteAnnouncer />
      <PageViewTracker />
      <CookieConsentBanner />
      <Toaster position="bottom-right" />
      <AppRoutes />
    </BrowserRouter>
  );
}
